import type { ElementItem } from '@atlaskit/smart-card';

import type { NLPSearchResultSource } from '../../ai-answer-dialog/types';

// Object mappings for product types that we will dynamically add data to the card
const PRODUCT_SOURCE_BLOCK_MAPPING = {
	Confluence: 'Confluence',
	Jira: 'Jira',
	Slack: 'Slack',
	Trello: 'Trello',
};

export enum ElementName {
	AppliedToComponentsCount = 'AppliedToComponentsCount',
	AssignedTo = 'AssignedTo',
	AssignedToGroup = 'AssignedToGroup',
	AttachmentCount = 'AttachmentCount',
	AuthorGroup = 'AuthorGroup',
	ChecklistProgress = 'ChecklistProgress',
	CollaboratorGroup = 'CollaboratorGroup',
	CommentCount = 'CommentCount',
	CreatedBy = 'CreatedBy',
	CreatedOn = 'CreatedOn',
	DueOn = 'DueOn',
	LatestCommit = 'LatestCommit',
	LinkIcon = 'LinkIcon',
	Location = 'Location',
	ModifiedBy = 'ModifiedBy',
	ModifiedOn = 'ModifiedOn',
	OwnedBy = 'OwnedBy',
	OwnedByGroup = 'OwnedByGroup',
	Preview = 'Preview',
	Priority = 'Priority',
	Provider = 'Provider',
	ReadTime = 'ReadTime',
	Snippet = 'Snippet',
	State = 'State',
	SubscriberCount = 'SubscriberCount',
	SubTasksProgress = 'SubTasksProgress',
	StoryPoints = 'StoryPoints',
	TargetBranch = 'TargetBranch',
	TeamMemberCount = 'TeamMemberCount',
	Title = 'Title',
	UserAttributes = 'UserAttributes',
	VoteCount = 'VoteCount',
	SentOn = 'SentOn',
}

const baseTopMetadata: ElementItem[] = [
	{ name: ElementName.ModifiedOn },
	{ name: ElementName.AttachmentCount },
	{ name: ElementName.SubscriberCount },
	{ name: ElementName.VoteCount },
	{ name: ElementName.DueOn },
	{ name: ElementName.ReadTime },
];

type SimulatedMetadata = {
	titleMetadata: ElementItem[];
	topMetadata: ElementItem[];
};

export const getDynamicSourceMetadata = (source: NLPSearchResultSource): SimulatedMetadata => {
	const defaultTitleMetadata: ElementItem[] = [{ name: ElementName.State }];
	const defaultTopMetadata: ElementItem[] = [
		{ name: ElementName.AuthorGroup },
		{ name: ElementName.CreatedBy },
		...baseTopMetadata,
	];

	switch (source.product) {
		case PRODUCT_SOURCE_BLOCK_MAPPING.Confluence:
			return {
				titleMetadata: defaultTitleMetadata,
				topMetadata: [
					{ name: ElementName.OwnedByGroup },
					{ name: ElementName.OwnedBy },
					...baseTopMetadata,
				],
			};
		case PRODUCT_SOURCE_BLOCK_MAPPING.Jira:
			const isJiraTask = source.type?.includes('issue') ?? false;

			let topMetadata: ElementItem[] = [
				{ name: ElementName.AuthorGroup },
				{ name: ElementName.CreatedBy },
				{ name: ElementName.ModifiedOn },
			];

			if (isJiraTask) {
				topMetadata = [
					{ name: ElementName.AssignedToGroup },
					{ name: ElementName.AssignedTo },
					{ name: ElementName.ModifiedOn },
				];
			}

			return {
				titleMetadata: defaultTitleMetadata,
				topMetadata,
			};
		case PRODUCT_SOURCE_BLOCK_MAPPING.Slack:
			return {
				titleMetadata: defaultTitleMetadata,
				topMetadata: [{ name: ElementName.AuthorGroup }, { name: ElementName.SentOn }],
			};
		case PRODUCT_SOURCE_BLOCK_MAPPING.Trello:
			return {
				titleMetadata: defaultTitleMetadata,
				topMetadata: [{ name: ElementName.CollaboratorGroup }, ...baseTopMetadata],
			};
		default:
			return {
				titleMetadata: defaultTitleMetadata,
				topMetadata: defaultTopMetadata,
			};
	}
};
